import type { RirekishoData } from '@src/api/core/api-types';
import { DATA_EXPORT_VERSION } from '@src/constants/version';
import type { RootState } from '@src/store';
import { getCurrentDateString } from '@src/utils/dateUtils';
import { createSelector } from 'reselect';

/**
 * Redux状態用の履歴書データメモ化セレクター
 *
 * reselectライブラリを使用してRedux状態から効率的に履歴書データを選択するセレクター群です。
 * 不要な再レンダリングを防ぐため、関連する状態が変更された場合のみ新しい値を返します。
 *
 * @fileoverview 履歴書データのメモ化セレクター定義
 * @since 1.0.0
 * @author Resume Commit Team
 */

/**
 * 基本セレクター群
 *
 * RootStateから個別の履歴書状態スライスを取得する基本セレクターです。
 * これらのセレクターは createSelector の入力セレクターとして使用されます。
 */

/**
 * 履歴書基本情報セレクター
 */
export const selectRirekishoBasicInfo = (state: RootState) => state.rirekisho.basicInfo;

/**
 * 履歴書写真情報セレクター
 */
export const selectRirekishoPhoto = (state: RootState) => state.rirekisho.photo;

/**
 * 履歴書住所情報セレクター
 */
export const selectRirekishoAddress = (state: RootState) => state.rirekisho.currentAddress;

/**
 * 履歴書連絡先情報セレクター
 */
export const selectRirekishoContact = (state: RootState) => state.rirekisho.contact;

/**
 * 履歴書学歴・職歴情報セレクター
 */
export const selectRirekishoHistory = (state: RootState) => state.rirekisho.history;

/**
 * 履歴書免許・資格情報セレクター
 */
export const selectRirekishoLicense = (state: RootState) => state.rirekisho.licenses;

/**
 * 履歴書志望動機・特技情報セレクター
 */
export const selectRirekishoMotivation = (state: RootState) => state.rirekisho.motivation;

/**
 * 履歴書本人希望記入欄セレクター
 */
export const selectRirekishoPersonalRequests = (state: RootState) =>
  state.rirekisho.personalRequests;

/**
 * 履歴書オプション設定セレクター
 */
export const selectRirekishoOptions = (state: RootState) => state.rirekisho.options;

/**
 * 履歴書入力状況セレクター
 */
export const selectRirekishoInputStatus = (state: RootState) => state.unifiedInputStatus.rirekisho;

/**
 * メモ化された履歴書データセレクター
 *
 * Redux状態から履歴書作成に必要なすべてのデータを効率的に取得するメモ化セレクターです。
 * reselectライブラリの createSelector を使用し、入力セレクターの値が変更された場合のみ
 * 新しいオブジェクトを返します。これにより不要な再レンダリングを防ぎ、
 * アプリケーションのパフォーマンスを最適化します。
 *
 * @example
 * ```tsx
 * import { useSelector } from 'react-redux';
 * import { selectRirekishoData } from '@src/selectors/rirekishoDataSelector';
 *
 * function MyComponent() {
 *   const rirekishoData = useSelector(selectRirekishoData);
 *
 *   return (
 *     <div>
 *       <h1>{rirekishoData.basicInfo.firstName} {rirekishoData.basicInfo.lastName}</h1>
 *       <p>学歴・職歴数: {rirekishoData.history.length}</p>
 *       <p>免許・資格数: {rirekishoData.licenses.length}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @returns {RirekishoData} 統合された履歴書データ
 *
 * @since 1.0.0
 * @author Resume Commit Team
 */
export const selectRirekishoData = createSelector(
  [
    selectRirekishoBasicInfo,
    selectRirekishoPhoto,
    selectRirekishoAddress,
    selectRirekishoContact,
    selectRirekishoHistory,
    selectRirekishoLicense,
    selectRirekishoMotivation,
    selectRirekishoPersonalRequests,
    selectRirekishoOptions,
    selectRirekishoInputStatus,
  ],
  (
    basicInfo,
    photo,
    address,
    contact,
    history,
    license,
    motivation,
    personalRequests,
    options,
    _inputStatus
  ): RirekishoData => ({
    version: DATA_EXPORT_VERSION,
    basicInfo: basicInfo || {
      lastName: '',
      firstName: '',
      lastNameReading: '',
      firstNameReading: '',
      gender: 'male',
      birthDate: '',
      currentDate: getCurrentDateString(),
      age: 0,
    },
    photo: photo || {
      photoPath: '',
    },
    currentAddress: address || {
      zipCode: '',
      address: '',
      addressReading: '',
      areaCode: '',
      phoneNumber: '',
      callInfo: '',
    },
    contact: contact || {
      zipCode: '',
      contact: '',
      contactReading: '',
      areaCode: '',
      phoneNumber: '',
      callInfo: '',
    },
    history: history || [],
    licenses: license || [],
    motivation: motivation || {
      body: '',
    },
    personalRequests: personalRequests || {
      requests: [],
      additionalNote: '',
    },
    options: options || {
      insertBlankLine: false,
      showGender: true,
      showPhoto: true,
    },
  })
);
